import classNames from 'classnames';
import React from 'react';
import { statusToneStyle, UDStatusTone } from 'ud-ui/components/status-tone';
import UDText, { UDTextVariant } from 'ud-ui/components/text';

/** Исход, который сообщает бейдж. Тот же словарь, что у фильтр-чипа (`UDFilterChip`). */
export type UDStatusBadgeTone = UDStatusTone;

export type UDStatusBadgeSize = 'sm' | 'md';

export interface UDStatusBadgeProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'color'> {
  tone: UDStatusBadgeTone;
  /** `sm` — шапка карточки, плитка; `md` — строка таблицы. */
  size?: UDStatusBadgeSize;
  children: React.ReactNode;
}

/**
 * Форма и типографика бейджа по размерам. Экспортируется для скелетонов: плашка
 * меряется теми же классами, что и живой бейдж (`.claude/docs/skeleton-kit.md`).
 *
 * Мельче `text-2` (14 px) в шкале нет, поэтому `sm` отличается только высотой и полями.
 */
export const UD_STATUS_BADGE_METRICS: Record<UDStatusBadgeSize, { className: string; variant: UDTextVariant }> = {
  sm: { className: 'rounded-pill inline-flex h-6 shrink-0 items-center px-2 whitespace-nowrap', variant: 'text-2' },
  md: { className: 'rounded-pill inline-flex h-7 shrink-0 items-center px-3 whitespace-nowrap', variant: 'text-2' },
};

/**
 * Бейдж статуса: пилюля со словом и цветом исхода.
 *
 * Цвет — переменные тона (`ud-ui/components/status-tone`), общие с фильтр-чипом: исход
 * выглядит одинаково и в бейдже, и в фильтре по нему.
 *
 * Не интерактивен и не live-region: статус не меняется на глазах, фокус и
 * `role="status"` ему не нужны (в отличие от `UDChip`). Атрибуты (`data-testid`,
 * `data-state`, `title`) уходят на корневой `span`.
 */
const UDStatusBadge = ({ tone, size = 'sm', className, style, children, ...rest }: UDStatusBadgeProps) => {
  const { className: sizeClassName, variant } = UD_STATUS_BADGE_METRICS[size];

  return (
    <UDText
      {...rest}
      as="span"
      variant={variant}
      data-tone={tone}
      style={{ ...statusToneStyle(tone), ...style }}
      className={classNames(sizeClassName, 'bg-(--tone-surface) text-(--tone-ink)', className)}
    >
      {children}
    </UDText>
  );
};

export default React.memo(UDStatusBadge);
